// cache.interceptor.ts
import { Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpResponse,
    HttpHeaders
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { JwtService } from '../services/jwt.service';
import { sentrySafeHttpPath, shouldEmitSentryBreadcrumb } from '../utils/sentry-telemetry.util';

/**
 * Configuration entry for cacheable endpoints.
 * - pattern: a RegExp to match the request URL (use urlWithParams or url path as needed).
 * - method?: HTTP method to match (e.g. 'GET' or 'POST'). If omitted, defaults to 'GET'.
 * - timeout?: in seconds; if provided, cached entries older than this will be refetched.
 */
interface CacheableEntry {
    pattern: RegExp;
    method?: string;
    timeout?: number;
    invalidatePatterns?: {
        pattern: RegExp,
        method?: string,
    }[];
}

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
    private cacheableUrls: CacheableEntry[] = [
        {
            pattern: /\/Structure\/Company\/default(\?.*)?$/,
            invalidatePatterns: [
                { pattern: /\/Structure\/Company$/, method: 'PUT' },
            ]
        },

        { pattern: /\/Shift\/current(\?.*)?$/, timeout: 10 * 60 },
        {
            pattern: /\/Structure\/Branch\/default(\?.*)?$/,
            timeout: 3 * 60,
            invalidatePatterns: [
                { pattern: /\/Structure\/Branch$/, method: 'PUT' },
            ]
        },
        {
            pattern: /\/Product\/\d+(\?.*)?$/, timeout: 60 * 60,
            invalidatePatterns: [
                { pattern: /\/Product$/, method: 'PUT' },
                { pattern: /\/Product$/, method: 'DELETE' }
            ]
        },

        // {
        //     pattern: /\/Product\/List(\?.*)?$/,
        //     method: 'POST', timeout: 30 * 60,
        //     invalidatePatterns: [
        //         { pattern: /\/Product$/, method: 'POST' },
        //         { pattern: /\/Product$/, method: 'PUT' }
        //     ]
        // },
        {
            pattern: /\/Customer\/list(\?.*)?$/, method: 'POST', timeout: 10 * 60,
            invalidatePatterns: [
                { pattern: /\/Customer$/, method: 'POST' },
                { pattern: /\/Customer$/, method: 'PUT' }
            ]
        },
        {
            pattern: /\/Document\/OpeningStock\/\d+(\?.*)?$/, timeout: 24 * 60 * 60,
            invalidatePatterns: [
                { pattern: /\/Document\/OpeningStock$/, method: 'POST' },
                { pattern: /\/Document\/OpeningStock$/, method: 'PUT' },
                { pattern: /\/Document\/OpeningStock$/, method: 'DELETE' }
            ]
        },
    ];

    constructor(
        private router: Router,
        private jwtService: JwtService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const useCacheHeader = req.headers.get('X-Use-Cache');
        const useCache = useCacheHeader === 'true';

        const invalidationEntries = this.cacheableUrls.filter(entry =>
            entry.invalidatePatterns?.some(inv => {
                const urlMatches = inv.pattern.test(req.urlWithParams);
                const methodMatches = !inv.method || inv.method.toUpperCase() === req.method.toUpperCase();
                return urlMatches && methodMatches;
            })
        );
        if (invalidationEntries.length > 0) {
            return next.handle(req).pipe(
                tap(event => {
                    if (event instanceof HttpResponse) {
                        invalidationEntries.forEach(entry => {
                            this.invalidateCacheForPattern(entry.pattern);
                        });
                        this.trySentryCacheBreadcrumb('invalidate', req);
                    }
                })
            );
        }

        if (!useCache) {
            return next.handle(req);
        }

        const entry = this.findCacheableEntry(req);

        if (!entry) {
            // Not cacheable
            return next.handle(req);
        }

        // Build a cache key that includes URL (with params for GET) and for POST also includes body serialization
        const cacheKey = this.getCacheKey(req);

        // If no tenant context, skip caching
        if (!cacheKey) {
            return next.handle(req);
        }

        // Try to retrieve from localStorage
        const cachedResponse = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(cacheKey + "_lastFetch");
        const timeout = entry.timeout;


        // If we have a timeout configured, check expiration
        if (cachedTimestamp && timeout) {
            try {
                const lastFetch = JSON.parse(cachedTimestamp) as number;
                const now = Date.now();
                const ageSeconds = (now - lastFetch) / 1000;

                if (ageSeconds > timeout) {
                    // expired: proceed to fetch & update cache
                    return this.handleAndCache(req, next, cacheKey);
                }
                // else: still within TTL; fall through to return cached if available
            } catch (e) {
                // parsing error: treat as no valid cache
                return this.handleAndCache(req, next, cacheKey);
            }
        }

        if (cachedResponse) {
            // We have a cached entry and it's not expired (or no timeout configured)
            try {
                const parsed = JSON.parse(cachedResponse);
                const { body, headers, status, statusText, url } = parsed;
                this.trySentryCacheBreadcrumb('hit', req);
                return of(new HttpResponse({
                    body,
                    headers: new HttpHeaders(headers),
                    status,
                    statusText,
                    url
                }));
            } catch (e) {
                // parsing error: refetch and overwrite
                console.log(e, " error")
                return this.handleAndCache(req, next, cacheKey);
            }
        }

        // No cached entry at all: fetch and cache
        return this.handleAndCache(req, next, cacheKey);
    }

    /**
     * Find a cacheable entry matching both URL and method.
     */
    private findCacheableEntry(req: HttpRequest<any>): CacheableEntry | undefined {
        // For matching, use req.urlWithParams for GET; for POST, use req.url (body not in URL).
        // But regex can be written to ignore query params via (\?.*)? as above.
        const urlToMatch = req.urlWithParams;
        // Find an entry whose method matches (or default GET) and whose pattern matches the URL
        return this.cacheableUrls.find(entry => {
            const method = entry.method ?? 'GET';
            if (method.toUpperCase() !== req.method.toUpperCase()) {
                return false;
            }
            return entry.pattern.test(urlToMatch);
        });
    }

    /**
     * Build a unique cache key string for a request with tenant context:
     * - Format: tenantId|method|url|bodyKey (for POST) or tenantId|method|urlWithParams (for GET)
     * - GET: use urlWithParams.
     * - POST: use url plus a canonical serialization of the body.
     */
    private getCacheKey(req: HttpRequest<any>): string {
        const token = sessionStorage.getItem('accessToken');
        const tenantId = token ? this.jwtService.getTenantId(token) : null;
        if (!tenantId) {
            console.warn('CacheInterceptor: No tenant ID available, skipping cache');
            return '';
        }

        const method = req.method.toUpperCase();
        
        if (req.method === 'GET') {
            return `${tenantId}|${method}|${req.urlWithParams}`;
        } else if (req.method === 'POST') {
            // Canonicalize body: stable JSON stringify (sort keys) so that logically equal payloads yield same key.
            const body = req.body;
            let bodyKey: string;
            try {
                bodyKey = this.stableStringify(body);
            } catch (e) {
                // Fallback: naive stringify
                bodyKey = JSON.stringify(body);
            }
            return `${tenantId}|${method}|${req.url}|${bodyKey}`;
        }
        // Fallback for other methods, though we don't hit here
        return `${tenantId}|${method}|${req.urlWithParams}`;
    }

    /**
     * Handle the request through next.handle and cache the HttpResponse when it arrives.
     */
    private handleAndCache(req: HttpRequest<any>, next: HttpHandler, cacheKey: string): Observable<HttpEvent<any>> {
        this.trySentryCacheBreadcrumb('fetch', req);
        return next.handle(req).pipe(
            tap(event => {
                if (event instanceof HttpResponse) {
                    // Serialize headers into a plain object
                    const serializedHeaders = this.serializeHeaders(event.headers);
                    const cacheData = {
                        body: event.body,
                        headers: serializedHeaders,
                        status: event.status,
                        statusText: event.statusText,
                        url: event.url
                    };
                    try {
                        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
                        localStorage.setItem(cacheKey + "_lastFetch", JSON.stringify(Date.now()));
                    } catch (e) {
                        // Storage quota exceeded or other error: consider pruning old entries or skip caching.
                        console.warn('CacheInterceptor: could not store cache entry', e);
                    }
                }
            })
        );
    }

    /**
     * Invalidate all cache entries whose URL part matches the given pattern for the current tenant.
     * It iterates over localStorage keys, extracts the URL portion from tenant-aware cache keys,
     * tests it against pattern, and removes matching entries and their "_lastFetch".
     */
    private invalidateCacheForPattern(pattern: RegExp): void {
        const token = sessionStorage.getItem('accessToken');
        const tenantId = token ? this.jwtService.getTenantId(token) : null;
        if (!tenantId) {
            console.warn('CacheInterceptor: No tenant ID available for cache invalidation');
            return;
        }

        const keysToRemove: string[] = [];
        const tenantPrefix = `${tenantId}|`;
        
        for (let i = 0; i < localStorage.length; i++) {
            const fullKey = localStorage.key(i);
            if (!fullKey) {
                continue;
            }
            
            // Skip keys that are only timestamps (we'll remove them alongside their data keys)
            if (fullKey.endsWith('_lastFetch')) {
                continue;
            }
            
            // Only process keys that belong to current tenant
            if (!fullKey.startsWith(tenantPrefix)) {
                continue;
            }
            
            // Extract URL part from tenant-aware cache key format: tenantId|method|url|bodyKey
            const keyParts = fullKey.split('|');
            if (keyParts.length >= 3) {
                const urlPart = keyParts[2]; // URL is the third part
                if (pattern.test(urlPart)) {
                    keysToRemove.push(fullKey);
                }
            }
        }
        
        // Remove collected keys and their timestamp entries
        keysToRemove.forEach(key => {
            try {
                localStorage.removeItem(key);
                localStorage.removeItem(key + '_lastFetch');
            } catch (e) {
                console.warn('CacheInterceptor: could not remove cache key', key, e);
            }
        });
    }

    /**
     * Serialize HttpHeaders into a plain object for JSON storage.
     */
    private serializeHeaders(headers: HttpHeaders): { [key: string]: string } {
        const serialized: { [key: string]: string } = {};
        headers.keys().forEach(key => {
            const value = headers.get(key);
            if (value !== null) {
                serialized[key] = value;
            }
        });
        return serialized;
    }

    /**
     * Produces a stable JSON string of an object by sorting object keys recursively.
     * This helps ensure that payloads with the same content but different key ordering yield the same string.
     */
    private stableStringify(obj: any): string {
        return JSON.stringify(this.canonicalize(obj));
    }

    /**
     * Recursively canonicalize an object/array:
     * - For objects: sort keys, apply canonicalize to values.
     * - For arrays: canonicalize each element.
     * - Primitives returned as-is.
     */
    private canonicalize(value: any): any {
        if (value === null || typeof value !== 'object') {
            // primitives (string, number, boolean, null)
            return value;
        }
        if (Array.isArray(value)) {
            return value.map(item => this.canonicalize(item));
        }
        // It's an object: sort keys
        const sortedKeys = Object.keys(value).sort();
        const result: any = {};
        for (const key of sortedKeys) {
            result[key] = this.canonicalize(value[key]);
        }
        return result;
    }

    /**
     * Clear all cache entries for a specific tenant
     */
    public clearTenantCache(tenantId?: string): void {
        const token = sessionStorage.getItem('accessToken');
        const targetTenantId = tenantId || (token ? this.jwtService.getTenantId(token) : null);
        if (!targetTenantId) {
            console.warn('CacheInterceptor: No tenant ID provided for cache clearing');
            return;
        }

        const keysToRemove: string[] = [];
        const tenantPrefix = `${targetTenantId}|`;
        
        for (let i = 0; i < localStorage.length; i++) {
            const fullKey = localStorage.key(i);
            if (!fullKey) {
                continue;
            }
            
            // Remove both cache keys and timestamp keys for this tenant
            if (fullKey.startsWith(tenantPrefix) || 
                (fullKey.endsWith('_lastFetch') && fullKey.startsWith(tenantPrefix.replace('|', '')))) {
                keysToRemove.push(fullKey);
            }
        }
        
        // Remove collected keys
        keysToRemove.forEach(key => {
            try {
                localStorage.removeItem(key);
            } catch (e) {
                console.warn('CacheInterceptor: could not remove cache key', key, e);
            }
        });
        
        console.log(`CacheInterceptor: Cleared ${keysToRemove.length} cache entries for tenant ${targetTenantId}`);
    }

    /** Sentry breadcrumb: cache outcome + path only (no query/body). / تسجيل نتيجة الكاش دون query أو body */
    private trySentryCacheBreadcrumb(outcome: 'hit' | 'fetch' | 'invalidate', req: HttpRequest<any>): void {
        if (!shouldEmitSentryBreadcrumb()) {
            return;
        }
        const path = sentrySafeHttpPath(req.url);
        Sentry.addBreadcrumb({
            category: 'http.cache',
            message: `Client cache: ${outcome}`,
            level: 'info',
            data: {
                outcome,
                method: req.method,
                path,
            },
        });
    }
}
